// src/utils/helpers.ts
import { GeneralLedgerLine, AccountBalanceSummary } from "../dto/reports.dto";

/**
 * Formats a number as currency.
 * @param value The number to format.
 * @param currency The currency code (e.g., 'IDR', 'USD').
 * @param locale The locale (e.g., 'id-ID', 'en-US').
 */
export const formatCurrency = (
  value: number | string,
  currency: string = "IDR",
  locale: string = "id-ID",
): string => {
  const _value = typeof value === "string" ? parseFloat(value) : value;
  // Use toLocaleString for robust currency formatting
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(_value);
};

/**
 * Formats a Date object into a 'YYYY-MM-DD' string.
 * @param date The Date object to format.
 */
export const formatDate = (date: Date | string): string => {
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Sums the total debit and credit for a General Ledger report.
 * Filters out account headers and sums only journal item debits/credits.
 */
export const sumGeneralLedgerTotals = (data: GeneralLedgerLine[]) => {
  let totalDebit = 0;
  let totalCredit = 0;

  data.forEach((line) => {
    if (line.type === "account_header") {
      totalDebit += line.debitPeriod;
      totalCredit += line.creditPeriod;
    }
    // For individual journal items, they contribute to their account's total,
    // which is then summed up in the header. So we just sum the headers' period totals.
  });

  return { totalDebit, totalCredit };
};

/**
 * Groups Balance Sheet accounts into Assets, Liabilities, and Equity.
 * @param accounts Array of AccountBalanceSummary.
 */
export const groupBalanceSheetAccounts = (
  accounts: AccountBalanceSummary[],
) => {
  const assets: AccountBalanceSummary[] = [];
  const liabilities: AccountBalanceSummary[] = [];
  const equity: AccountBalanceSummary[] = [];

  let totalAssets = 0;
  let totalLiabilities = 0;
  let totalEquity = 0;

  accounts.forEach((account) => {
    // Odoo's internal groups and account types for classification
    if (
      ["asset"].includes(account.internalGroup) ||
      [
        "asset_receivable",
        "asset_cash",
        "asset_fixed",
        "asset_current",
      ].includes(account.accountType)
    ) {
      assets.push(account);
      totalAssets += account.balance;
    } else if (
      ["liability"].includes(account.internalGroup) ||
      ["liability_payable", "liability_current"].includes(account.accountType)
    ) {
      liabilities.push(account);
      totalLiabilities += account.balance;
    } else if (
      ["equity"].includes(account.internalGroup) ||
      ["equity"].includes(account.accountType)
    ) {
      equity.push(account);
      totalEquity += account.balance;
    }
  });

  // Sort by account code for better presentation
  assets.sort((a, b) => a.code.localeCompare(b.code));
  liabilities.sort((a, b) => a.code.localeCompare(b.code));
  equity.sort((a, b) => a.code.localeCompare(b.code));

  return {
    assets,
    liabilities,
    equity,
    totalAssets,
    totalLiabilities,
    totalEquity,
    totalLiabilitiesAndEquity: totalLiabilities + totalEquity,
  };
};

/**
 * Groups Profit & Loss accounts into Income and Expenses.
 * @param accounts Array of AccountBalanceSummary.
 */
export const groupProfitLossAccounts = (
  accounts: AccountBalanceSummary[],
  signFlip: boolean = false,
) => {
  const income: AccountBalanceSummary[] = [];
  const expenses: AccountBalanceSummary[] = [];
  const costOfRevenue: AccountBalanceSummary[] = [];
  const otherIncome: AccountBalanceSummary[] = [];

  let totalOperatingIncome = 0;
  let totalOtherIncome = 0;
  let totalCostOfRevenue = 0;
  let totalOperatingExpenses = 0;

  accounts.forEach((account) => {
    let adjustedBalance = account.balance;

    if (signFlip) {
      if (["income", "income_other"].includes(account.accountType)) {
        // Income → jika balance < 0 (normal income), flip jadi positif
        // Income → jika balance > 0 (pengurang income), flip jadi negatif
        adjustedBalance =
          account.balance < 0
            ? Math.abs(account.balance)
            : -Math.abs(account.balance);
      } else if (
        ["expense_direct_cost", "expense"].includes(account.accountType)
      ) {
        // Expense → jika balance > 0 (normal expense), flip jadi negatif
        // Expense → jika balance < 0 (pengurang expense / reimbursement), flip jadi positif
        adjustedBalance =
          account.balance > 0
            ? -Math.abs(account.balance)
            : Math.abs(account.balance);
      }
    }

    if (account.accountType === "income") {
      income.push({ ...account, balance: adjustedBalance });
      totalOperatingIncome += adjustedBalance;
    } else if (account.accountType === "income_other") {
      otherIncome.push({ ...account, balance: adjustedBalance });
      totalOtherIncome += adjustedBalance;
    } else if (account.accountType === "expense_direct_cost") {
      costOfRevenue.push({ ...account, balance: adjustedBalance });
      totalCostOfRevenue += adjustedBalance;
    } else if (account.accountType === "expense") {
      expenses.push({ ...account, balance: adjustedBalance });
      totalOperatingExpenses += adjustedBalance;
    }
  });

  // Sorting
  [income, otherIncome, costOfRevenue, expenses].forEach((arr) =>
    arr.sort((a, b) => a.code.localeCompare(b.code)),
  );

  const totalIncome = totalOperatingIncome + totalOtherIncome;
  const totalGrossProfit = totalOperatingIncome + totalCostOfRevenue;
  let netProfitLoss =
    totalGrossProfit + totalOtherIncome + totalOperatingExpenses;

  if (!signFlip) {
    netProfitLoss *= -1;
  }

  return {
    income,
    expenses,
    costOfRevenue,
    otherIncome,
    totalIncome,
    totalOperatingIncome,
    totalOtherIncome,
    totalCostOfRevenue,
    totalOperatingExpenses,
    totalGrossProfit,
    netProfitLoss,
  };
};

export function getStartOfMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

export function formatLocaleDate(date: string) {
  const locale = "id-ID";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
  }).format(new Date(date));
}

export const percentageFormatter = (number: number, decimals = 1) => {
  const formattedNumber = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
  const symbol = number > 0 && number !== Infinity ? "+" : "";

  return `${symbol}${formattedNumber}`;
};
